import React, { forwardRef, useContext, useState } from 'react';
import { useDynamicSVGImport } from './hook';
import { TIconSize } from '@src/models/common';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { classnames } from '@src/utils/classnames';
import styled from './Icon.module.scss';
import { getClasses } from '@src/utils/getClass';
import { getAdaptiveIconsSize } from '@src/atoms/Icon/utils';
import { Link } from '@src/atoms/Link';
import { IDefaultLinkProps, TLinkTarget } from '@src/atoms/Link/models';
import { TIconName } from './models';
import { IClassNameProps, IStylesProps } from '@src/models/props';
import { IIdentify } from '@src/models/analytics';
import { TColorNew } from '@src/models/colors';
import { getColorClass } from '@src/utils/colorUtils';

export interface IIconComponentProps extends IClassNameProps, IStylesProps, React.SVGProps<SVGSVGElement> {
  view?: TColorNew;
}

export interface IIconProps extends IDefaultLinkProps, IIconComponentProps, IIdentify {
  name: TIconName;
  target?: TLinkTarget;
  size?: TIconSize;
  disabled?: boolean;
}

/**
 * Компонент для отображения иконки
 * @deprecated используй новый компонент `<IconsV2 />`
 * Используй для отображения иконок при верстке новых макетов
 */
export const Icon = forwardRef<SVGSVGElement, IIconProps>(
  ({ name, href, id, view = 'basicBlack', size = 'md', className, disableNextJs, target, disabled, ...props }, ref) => {
    /** Содержит в себе ReactNode с данными иконки. Используем свойство props, которое и позволят отрисовать иконку */
    const [svgData, setSvgData] = useState<{ props: React.SVGProps<SVGSVGElement> }>();

    const { breakpoint } = useContext(BreakpointsContext);
    const iconDataId = href && id ? undefined : id;
    const linkIconDataId = id || `icon-${name}`;

    const classes = classnames(
      className,
      styled.root,
      ...getClasses(
        styled,
        {
          size,
        },
        breakpoint,
      ),
      `Icon__name_${name}`,
      getColorClass({ view, property: 'color' }),
      disabled && styled.disabled,
    );

    const style =
      typeof size === 'number' || typeof size === 'object'
        ? { ...props.style, ...getAdaptiveIconsSize(size, breakpoint) }
        : props.style;

    useDynamicSVGImport({ name, onCompleted: setSvgData });

    if (!svgData) return <div className={classes} />;

    return (
      <Link.Wrapper id={linkIconDataId} disableNextJs={disableNextJs} target={target} href={href}>
        <svg {...svgData.props} className={classes} {...props} style={style} data-id={iconDataId} ref={ref} />
      </Link.Wrapper>
    );
  },
);

Icon.displayName = 'Icon';
